import type { CSSProperties } from "react"

type RNStyle = Record<string, unknown> | RNStyle[] | null | undefined | false

const EXPAND_PROPS: Record<string, [string, string]> = {
  marginHorizontal: ["marginLeft", "marginRight"],
  marginVertical: ["marginTop", "marginBottom"],
  paddingHorizontal: ["paddingLeft", "paddingRight"],
  paddingVertical: ["paddingTop", "paddingBottom"],
}

/** RN 单边 border 写入 style 对象时转为 CSS */
const BORDER_SIDE_KEYS = [
  "borderTopWidth",
  "borderTopStyle",
  "borderTopColor",
  "borderRightWidth",
  "borderRightStyle",
  "borderRightColor",
  "borderBottomWidth",
  "borderBottomStyle",
  "borderBottomColor",
  "borderLeftWidth",
  "borderLeftStyle",
  "borderLeftColor",
] as const

const BORDER_RADIUS_KEYS = [
  "borderTopLeftRadius",
  "borderTopRightRadius",
  "borderBottomLeftRadius",
  "borderBottomRightRadius",
] as const

function addPx(value: unknown, key: string): string | number | undefined {
  if (value == null) return undefined
  if (typeof value === "string") return value
  if (typeof value !== "number") return value as number
  if (key === "flex" || key === "opacity" || key === "zIndex" || key === "fontWeight") {
    return value
  }
  if (key.startsWith("flex") && key !== "flexGrow" && key !== "flexShrink" && key !== "flexBasis") {
    return value
  }
  return `${value}px`
}

function toCssLength(value: unknown): string {
  if (typeof value === "number") return `${value}px`
  return String(value ?? 0)
}

function transformArrayToCss(transform: unknown): string | undefined {
  if (!transform) return undefined
  if (typeof transform === "string") return transform
  if (!Array.isArray(transform)) return undefined

  const parts: string[] = []
  for (const item of transform) {
    if (!item || typeof item !== "object") continue
    for (const [key, raw] of Object.entries(item as Record<string, unknown>)) {
      if (raw == null) continue
      switch (key) {
        case "translateX":
          parts.push(`translateX(${toCssLength(raw)})`)
          break
        case "translateY":
          parts.push(`translateY(${toCssLength(raw)})`)
          break
        case "translateZ":
          parts.push(`translateZ(${toCssLength(raw)})`)
          break
        case "scale":
        case "scaleX":
        case "scaleY":
          parts.push(`${key}(${raw})`)
          break
        case "rotate":
        case "rotateX":
        case "rotateY":
        case "rotateZ":
        case "skewX":
        case "skewY":
          parts.push(`${key}(${typeof raw === "number" ? `${raw}deg` : raw})`)
          break
        case "perspective":
          parts.push(`perspective(${toCssLength(raw)})`)
          break
        default:
          parts.push(`${key}(${raw})`)
      }
    }
  }
  return parts.length ? parts.join(" ") : undefined
}

function expandStyle(style: Record<string, unknown>): CSSProperties {
  const result: Record<string, unknown> = {}

  for (const [key, raw] of Object.entries(style)) {
    if (raw == null) continue

    const expand = EXPAND_PROPS[key]
    if (expand) {
      result[expand[0]] = addPx(raw, expand[0])
      result[expand[1]] = addPx(raw, expand[1])
      continue
    }

    if (key === "shadowColor" || key === "shadowOffset" || key === "shadowOpacity" || key === "shadowRadius") {
      continue
    }

    if (key === "elevation") {
      continue
    }

    if ((BORDER_SIDE_KEYS as readonly string[]).includes(key)) {
      result[key] = key.endsWith("Width") ? addPx(raw, key) : raw
      continue
    }

    if ((BORDER_RADIUS_KEYS as readonly string[]).includes(key)) {
      result[key] = addPx(raw, key)
      continue
    }

    if (key === "transform") {
      const css = transformArrayToCss(raw)
      if (css) result.transform = css
      continue
    }

    result[key] = addPx(raw, key)
  }

  const { shadowColor, shadowOffset, shadowOpacity, shadowRadius } = style
  if (shadowColor && shadowOffset && typeof shadowOffset === "object") {
    const offset = shadowOffset as { width?: number; height?: number }
    const opacity = typeof shadowOpacity === "number" ? shadowOpacity : 0.2
    const radius = typeof shadowRadius === "number" ? shadowRadius : 2
    result.boxShadow = `${offset.width || 0}px ${offset.height || 0}px ${radius}px rgba(0,0,0,${opacity})`
  }

  return result as CSSProperties
}

export function flattenStyle(style?: RNStyle): CSSProperties {
  if (!style) return {}
  if (Array.isArray(style)) {
    return style.reduce<CSSProperties>((acc, item) => {
      return { ...acc, ...flattenStyle(item) }
    }, {})
  }
  return expandStyle(style as Record<string, unknown>)
}

export const StyleSheet = {
  create<T extends Record<string, Record<string, unknown>>>(styles: T): T {
    return styles
  },
  flatten: flattenStyle,
  absoluteFillObject: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  hairlineWidth: 1,
}
